/* eslint-disable max-classes-per-file */
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { DOMSerializer, DOMParser as PMDOMParser } from '@tiptap/pm/model';
import { renderLabel } from '@/vuex/project/mention-helper';

const key = new PluginKey('pasteHandler');

/**
 * A custom DOM serializer to add a custom attribute in the copied HTML
 * that holds the project hash
 */
class CustomDOMSerializer {
  constructor(schema, hash) {
    this.serializer = DOMSerializer.fromSchema(schema);
    this.hash = hash;
  }

  // eslint-disable-next-line default-param-last
  serializeFragment(fragment, options = {}, target) {
    const customTarget = this.serializer.serializeFragment(
      fragment,
      options,
      target
    );

    // console.log('[pasteHandler] customSerializer:', customTarget);
    customTarget.firstChild.setAttribute('data-arcweave-project', this.hash);

    return customTarget;
  }
}

function initPasteHandler(store) {
  const PasteHandler = Extension.create({
    name: 'pasteHandler',

    addProseMirrorPlugins() {
      const hash = store.getters['project/getHash'];
      const serializer = new CustomDOMSerializer(this.editor.schema, hash);
      return [
        new Plugin({
          key,

          props: {
            clipboardSerializer: serializer,
            /**
             * Transforms the pasted HTML
             */
            transformPastedHTML: (html, view) => {
              const domHtml = new DOMParser().parseFromString(
                html,
                'text/html'
              );

              // The project hash of the clipboard data origin
              const clipboardHash = domHtml
                .querySelector('[data-arcweave-project]')
                ?.getAttribute('data-arcweave-project');

              // Search for mentions and if they don't exist, unwrap them
              const mentionNodes = domHtml.querySelectorAll('span.mention');
              mentionNodes.forEach(mention => {
                const mentionId = mention.getAttribute('data-id');
                const type = mention.getAttribute('data-type');
                const comp =
                  store.getters[`project/data/${type}s/getNode`](mentionId);
                if (clipboardHash !== hash || !comp) {
                  mention.replaceWith(mention.textContent);
                } else {
                  // Update the name/label of mention
                  const mentionOptions =
                    store.getters[`project/data/${type}s/getMention`](
                      mentionId
                    );
                  mention.setAttribute(
                    'data-label',
                    mentionOptions.mention_name
                  );
                  mention.textContent = renderLabel({
                    node: {
                      attrs: {
                        type,
                        label: mentionOptions.mention_name,
                        id: mentionId,
                      },
                    },
                  });
                }
              });

              // Get the <pre> elements and transform them to <pre><code></code></pre>
              const preCode = domHtml.querySelectorAll('pre');
              preCode.forEach(element => {
                // Split each line to it's own code block
                if (element.firstChild.nodeName === 'CODE') {
                  element = element.firstChild;
                }
                const lines = element.innerHTML.split('\n');
                const newElements = lines.map(line => {
                  const preNode = document.createElement('pre');
                  const codeNode = document.createElement('code');
                  codeNode.innerHTML = line;
                  preNode.appendChild(codeNode);
                  return preNode;
                });
                // Replace the old <pre> nodes with the new <pre><code></code></pre>
                newElements.forEach(newEl => {
                  element.parentElement.insertBefore(newEl, element);
                });
                element.remove();
              });

              return domHtml.documentElement.outerHTML;
            },
            /**
             * Prosemirror callback to handle paste.
             * Transforms each line to tiptap text content
             */
            handlePaste: (view, event, slice) => {
              // We are only handling pasting inside CustomCodeBlockLowLight nodes. The rest are handled by ProseMirror.
              if (
                view.state.selection.$head.parent.type.name !==
                'CustomCodeBlockLowLight'
              ) {
                return false;
              }

              let plainText = event.clipboardData.getData('text/plain');
              let htmlText = event.clipboardData.getData('text/html');

              if (htmlText) {
                htmlText = key
                  .get(view.state)
                  .props.transformPastedHTML(htmlText, view);
              } else {
                // Split clipboard data to lines
                plainText = plainText.replace(/\r\n/g, '\n');
                const lines = plainText.split('\n');

                // Replace the selection with the first line
                const { tr } = view.state;
                const firstNode = view.state.schema.text(lines[0]);
                tr.replaceSelectionWith(firstNode);

                // Add the rest of the lines in new code blocks
                lines.forEach((line, index) => {
                  if (index === 0) return;
                  const newNode = view.state.schema.node(
                    'CustomCodeBlockLowLight',
                    null,
                    view.state.schema.text(line)
                  );
                  tr.insert(tr.selection.$anchor.pos, newNode);
                  tr.setSelection(
                    TextSelection.create(
                      tr.doc,
                      tr.selection.$anchor.pos + newNode.nodeSize
                    )
                  );
                });

                view.dispatch(tr);
                return true;
              }
              return false;
            },
          },
        }),
      ];
    },
  });

  return PasteHandler;
}

export default initPasteHandler;
